import { RefObject } from "react";

import { Badge, Button, Card, Text, View } from "../base";
import { Icon } from "../base/Icon";
import { Organization } from "../../redux/reducers/organizationSlice";

type OrganizationGeneralCardProps = {
  organization: Organization | null | undefined;
  organizationId: string | undefined;
  isEditingName: boolean;
  editedName: string;
  onStartEditingName: () => void;
  onChangeName: (name: string) => void;
  onSaveName: () => void;
  onNameKeyDown: (e: React.KeyboardEvent) => void;
  nameInputRef: RefObject<HTMLInputElement>;
};

export const OrganizationGeneralCard = ({
  organization,
  isEditingName,
  editedName,
  onStartEditingName,
  onChangeName,
  onSaveName,
  onNameKeyDown,
  nameInputRef,
}: OrganizationGeneralCardProps) => {
  const tier = organization?.tier || "community";
  const isEnterprise = tier === "enterprise";

  return (
    <View className="flex flex-col gap-6">
      {/* Organization Name */}
      <View className="flex flex-col">
        <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-3">Organization Name</Text>
        <Card className="p-5">
          {isEditingName ? (
            <View className="flex flex-row items-center gap-3">
              <input
                ref={nameInputRef}
                value={editedName}
                onChange={(e) => onChangeName(e.target.value)}
                onBlur={onSaveName}
                onKeyDown={onNameKeyDown}
                className="flex-1 text-sm font-medium text-gray-900 dark:text-white bg-transparent border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 outline-none focus:border-blue-500 dark:focus:border-blue-400"
                maxLength={100}
              />
              <Button
                variant="success"
                size="auto"
                onClick={onSaveName}
              >
                Save
              </Button>
            </View>
          ) : (
            <View className="flex flex-row items-center justify-between">
              <Text className="text-sm font-medium text-gray-900 dark:text-white">{organization?.name || "Unnamed Organization"}</Text>
              <Button
                onClick={onStartEditingName}
                variant="ghost"
                size="auto"
                className="min-h-0 px-2 py-2"
              >
                <Icon
                  name="edit"
                  size={16}
                  className="text-gray-400 dark:text-gray-500"
                />
              </Button>
            </View>
          )}
        </Card>
      </View>

      {/* Current Plan */}
      <View className="flex flex-col">
        <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-3">Current Plan</Text>
        {isEnterprise ? (
          <Card className="overflow-hidden p-5 border border-cyan-200 bg-gradient-to-br from-cyan-50 via-white to-teal-50 shadow-sm dark:border-slate-700 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
            <View className="flex flex-col gap-4">
              <View className="flex flex-row items-start gap-3">
                <View className="h-10 w-10 pt-1 rounded-full bg-cyan-100 border border-cyan-200 flex flex-row items-center justify-center shrink-0 dark:bg-slate-800 dark:border-slate-600">
                  <Icon
                    name="diamond"
                    size={18}
                    className="text-cyan-700 dark:text-cyan-300"
                  />
                </View>
                <View className="flex flex-col gap-1">
                  <View className="flex flex-row items-center gap-2">
                    <Text className="text-sm font-semibold text-gray-900 dark:text-white">Enterprise</Text>
                    <Badge variant="success">Current Plan</Badge>
                  </View>
                  <Text className="text-xs text-gray-800 dark:text-gray-300">Built for production teams with mission-critical voice workloads.</Text>
                </View>
              </View>

              <View className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-2 border-t border-cyan-100 dark:border-slate-700">
                {[
                  { icon: "phone", label: "Unlimited phone numbers" },
                  { icon: "support_agent", label: "Dedicated account manager" },
                  { icon: "speed", label: "Volume-based pricing discounts" },
                  { icon: "engineering", label: "Forward deployed engineers" },
                  { icon: "cloud_done", label: "VPC or on-prem deployment" },
                  { icon: "verified_user", label: "Custom SLA and compliance" },
                ].map((item) => (
                  <View
                    key={item.label}
                    className="flex flex-row items-center gap-2.5 rounded-lg bg-white border border-gray-200 px-3 py-2.5 dark:bg-slate-800 dark:border-slate-700"
                  >
                    <Icon
                      name={item.icon}
                      size={14}
                      className="text-cyan-700 dark:text-cyan-300 shrink-0"
                    />
                    <Text className="text-xs text-gray-900 dark:text-gray-100 leading-relaxed">{item.label}</Text>
                  </View>
                ))}
              </View>
            </View>
          </Card>
        ) : (
          <Card className="p-5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900 transition-all duration-200 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600">
            <View className="flex flex-col gap-4">
              <View className="flex flex-row items-start justify-between gap-3">
                <View className="flex flex-row items-start gap-3">
                  <View className="h-10 w-10 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800 flex flex-row items-center justify-center shrink-0">
                    <Icon
                      name="users"
                      size={18}
                      className="text-gray-700 dark:text-gray-200"
                    />
                  </View>
                  <View className="flex flex-col gap-1">
                    <View className="flex flex-row items-center gap-2">
                      <Text className="text-sm font-semibold text-gray-900 dark:text-white">Community</Text>
                      <Badge variant="info">Current Plan</Badge>
                    </View>
                    <Text className="text-xs text-gray-950 dark:text-gray-100">Great for getting started with VoiceRun.</Text>
                  </View>
                </View>
              </View>

              <View className="flex flex-col gap-2 pt-3 border-t border-gray-100 dark:border-gray-800">
                {["Core voice agent features", "Starter usage limits", "Self-serve setup"].map((feature) => (
                  <View
                    key={feature}
                    className="flex flex-row items-center gap-2"
                  >
                    <Icon
                      name="check-circle"
                      size={14}
                      className="text-gray-800 dark:text-gray-200 shrink-0"
                    />
                    <Text className="text-xs text-gray-950 dark:text-gray-100">{feature}</Text>
                  </View>
                ))}
              </View>

              <View className="flex flex-col gap-2">
                <Text className="text-xs text-gray-950 dark:text-gray-100">Need higher scale and dedicated support?</Text>
                <a
                  href="https://voicerun.com/pricing/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                >
                  <View className="flex flex-row items-center justify-center gap-2.5 rounded-xl bg-slate-900 px-5 py-3 shadow-md shadow-slate-900/20 transition-all duration-200 group-hover:bg-slate-800 group-hover:shadow-lg dark:bg-brand-600 dark:group-hover:bg-brand-500">
                    <Icon
                      name="rocket"
                      size={16}
                      className="text-white"
                    />
                    <Text className="text-sm font-semibold text-white">Upgrade to Enterprise</Text>
                    <Icon
                      name="arrow_forward"
                      size={14}
                      className="text-white transition-transform duration-200 group-hover:translate-x-0.5"
                    />
                  </View>
                </a>
              </View>
            </View>
          </Card>
        )}
      </View>
    </View>
  );
};
